import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiCheck, mdiClose, mdiPencil } from '@mdi/js'
import type { RetentionRule } from '../../api/schema'
import { useCan } from '../../contexts/PermissionsContext'
import { useRetentionRules, useSaveRetentionRule } from '../../hooks/useDataProtection'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { AppIcon } from '../ui/icon'
import { Input } from '../ui/input'

/**
 * "Conservation des données" on the business rules page (spec 2026-10-03-02,
 * lot 3): one row per subject, an "overridden" badge on an organization rule.
 * An override may only shorten the catalogue value; the API says so.
 */
export function RetentionRulesSection({ org }: { org: string }) {
    const { t } = useTranslation('data-protection')
    const canRead = useCan('data_protection.read')
    const { data: rules = [], isLoading } = useRetentionRules(org, canRead)
    if (!canRead) return null
    return (
        <section className="mt-8" data-testid="retention-rules-section">
            <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">{t('rules.title')}</h2>
            <p className="text-sm text-muted-foreground mb-2">{t('rules.subtitle')}</p>
            {isLoading ? null : rules.length === 0 ? (
                <p className="text-sm text-muted-foreground border rounded-lg px-4 py-3" data-testid="retention-rules-empty">
                    {t('rules.empty')}
                </p>
            ) : (
                <div className="border rounded-lg divide-y">
                    {rules.map((r) => (
                        <RetentionRuleRow key={r.uid} org={org} rule={r} />
                    ))}
                </div>
            )}
        </section>
    )
}

function RetentionRuleRow({ org, rule }: { org: string; rule: RetentionRule }) {
    const { t } = useTranslation('data-protection')
    const canWrite = useCan('data_protection.write')
    const save = useSaveRetentionRule(org)
    const [editing, setEditing] = useState(false)
    const [value, setValue] = useState('')
    const duration = rule.permanent
        ? t('rules.permanent')
        : t(`rules.duration.${rule.duration_unit}`, { count: rule.duration_value ?? 0 })

    const submit = async () => {
        const n = Number(value)
        if (!Number.isInteger(n) || n < 0) return
        await save.mutateAsync({
            uid: rule.uid,
            body: {
                subject: rule.subject,
                purpose: rule.purpose,
                legal_basis: rule.legal_basis,
                duration_value: n,
                duration_unit: rule.duration_unit ?? 'year',
                permanent: false,
                anchor: rule.anchor,
                disposition: rule.disposition,
                reference: rule.reference,
            },
        })
        setEditing(false)
    }

    return (
        <div className="flex flex-wrap items-center gap-3 px-4 py-2.5" data-testid={`retention-rule-${rule.subject}`}>
            <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{rule.subject}</div>
                <div className="text-xs text-muted-foreground">
                    {rule.purpose} · {t(`rules.basis.${rule.legal_basis}`)} · {t(`rules.anchor.${rule.anchor}`)} ·{' '}
                    {t(`rules.disposition.${rule.disposition}`)}
                </div>
            </div>
            {rule.origin === 'organization' && (
                <Badge variant="info" data-testid={`retention-rule-overridden-${rule.subject}`}>
                    {t('rules.overridden')}
                </Badge>
            )}
            {editing ? (
                <div className="flex items-center gap-1">
                    <Input
                        type="number"
                        min={0}
                        className="h-8 w-20"
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        data-testid={`retention-rule-input-${rule.subject}`}
                    />
                    <span className="text-xs text-muted-foreground">{t(`rules.unit.${rule.duration_unit ?? 'year'}`)}</span>
                    <Button size="icon" variant="outline" className="h-8 w-8" onClick={submit} disabled={save.isPending}
                        aria-label={t('rules.save')} title={t('rules.save')} data-testid={`retention-rule-save-${rule.subject}`}>
                        <AppIcon path={mdiCheck} size="sm" />
                    </Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(false)}
                        aria-label={t('rules.cancel')} title={t('rules.cancel')} data-testid={`retention-rule-cancel-${rule.subject}`}>
                        <AppIcon path={mdiClose} size="sm" />
                    </Button>
                </div>
            ) : (
                <div className="flex items-center gap-1">
                    <span className="text-sm tabular-nums" data-testid={`retention-rule-duration-${rule.subject}`}>{duration}</span>
                    {canWrite && !rule.permanent && (
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-muted-foreground"
                            onClick={() => { setValue(String(rule.duration_value ?? '')); setEditing(true) }}
                            aria-label={t('rules.edit')} title={t('rules.edit')} data-testid={`retention-rule-edit-${rule.subject}`}>
                            <AppIcon path={mdiPencil} size="sm" />
                        </Button>
                    )}
                </div>
            )}
            {save.error && (
                <p className="basis-full text-xs text-destructive" data-testid={`retention-rule-error-${rule.subject}`}>
                    {save.error.message}
                </p>
            )}
        </div>
    )
}
